'use client';

import { useCallback, useEffect, useState } from 'react';
import supabase from '@/supabase';
import {
  createSignedStoryUrl,
  STORIES_MEDIA_BUCKET,
  STORIES_THUMBNAILS_BUCKET,
} from '@/lib/story-upload';
import type { StoryRailItem, StoryRailRow } from '@/types/story';

interface UseStoryRailOptions {
  userId?: string;
  enabled?: boolean;
}

interface UseStoryRailResult {
  stories: StoryRailItem[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useStoryRail({
  userId,
  enabled = true,
}: UseStoryRailOptions): UseStoryRailResult {
  const [stories, setStories] = useState<StoryRailItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStories = useCallback(async () => {
    if (!enabled || !userId) {
      setStories([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase.rpc('get_story_rail');

      if (fetchError) {
        throw fetchError;
      }

      const rows = (data || []) as StoryRailRow[];

      const hydratedStories = await Promise.all(
        rows.map(async (row) => {
          const [mediaUrl, thumbnailUrl] = await Promise.all([
            createSignedStoryUrl(STORIES_MEDIA_BUCKET, row.media_path),
            createSignedStoryUrl(STORIES_THUMBNAILS_BUCKET, row.thumbnail_path),
          ]);

          return {
            id: row.story_id,
            authorType: row.author_type,
            authorId: row.author_id,
            authorName: row.author_name || 'Unknown',
            authorAvatarUrl: row.author_avatar_url,
            mediaType: row.media_type,
            mediaUrl: mediaUrl || '',
            thumbnailUrl: thumbnailUrl || row.author_avatar_url || '/defaultAvatar.png',
            createdAt: row.created_at,
            expiresAt: row.expires_at,
            caption: row.caption,
            viewed: row.viewed,
            botId: row.bot_id,
            isOwn: row.author_type === 'user' && row.author_id === userId,
          } satisfies StoryRailItem;
        })
      );

      setStories(hydratedStories.filter((story) => Boolean(story.mediaUrl)));
    } catch (err) {
      console.error('[useStoryRail] Failed to fetch stories:', err);
      setError('Failed to load stories');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, userId]);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  return {
    stories,
    isLoading,
    error,
    refetch: fetchStories,
  };
}
